import { z } from "zod";

// ============================================================================
// Core Schemas - Simple, flat, extensible
// ============================================================================

// Model provider - constrained to what we support
export const ModelProviderSchema = z.enum(["openai", "anthropic"]);
export type ModelProvider = z.infer<typeof ModelProviderSchema>;

// Available models with their providers
export const AVAILABLE_MODELS = [
  { provider: "anthropic" as const, model: "claude-sonnet-4-20250514" },
  { provider: "openai" as const, model: "gpt-4.1-2025-04-14" },
] as const;

// Agent name validation - Docker-style naming
export const AgentNameSchema = z
  .string()
  .min(3, "Agent name must be at least 3 characters")
  .max(50, "Agent name must be at most 50 characters")
  .regex(
    /^[a-z0-9-]+$/,
    "Agent name must be lowercase letters, numbers, and hyphens only",
  );

// ============================================================================
// Tool Schema - Unified base schemas
// ============================================================================

// Base tool reference - minimal tool identification
export const BaseToolSchema = z.object({
  name: z
    .string()
    .min(1)
    .regex(/^[a-zA-Z0-9_-]+$/),
  namespace: z.string().min(1), // "native" or mcp_connection_id
});

// Tool with enablement state
export const ToolWithEnabledSchema = BaseToolSchema.extend({
  enabled: z.boolean().default(false),
});

// Stored tool with database ID
export const StoredToolSchema = ToolWithEnabledSchema.extend({
  id: z.string().uuid(),
});

// Tool with optional server ID for MCP integration
export const ToolWithServerIdSchema = z.object({
  name: z.string(),
  serverId: z.string().optional(), // Optional because native tools don't include this field
});

export type BaseTool = z.infer<typeof BaseToolSchema>;
export type ToolWithEnabled = z.infer<typeof ToolWithEnabledSchema>;
export type StoredTool = z.infer<typeof StoredToolSchema>;
export type ToolWithServerId = z.infer<typeof ToolWithServerIdSchema>;

// MCP Server entry with connection state and capabilities
export const McpServerEntrySchema = z.object({
  name: z.string(),
  server_url: z.string(),
  auth_url: z.string().nullable(),
  state: z.enum([
    "authenticating",
    "connecting",
    "ready",
    "discovering",
    "failed",
  ]),
  instructions: z.string().nullable(),
  capabilities: z.object({
    tools: z.array(z.string()),
    resources: z.array(z.string()),
  }),
});

export type McpServerEntry = z.infer<typeof McpServerEntrySchema>;

// ============================================================================
// API Response Schemas - For REST endpoints
// ============================================================================

// Native tool response schema - just the base tool with enabled state and description
export const NativeToolResponseSchema = ToolWithEnabledSchema.extend({
  description: z.string(),
});

// MCP tool response schema - extends base with MCP-specific fields
export const McpToolResponseSchema = ToolWithEnabledSchema.extend({
  serverId: z.string(),
  serverName: z.string(),
  description: z.string(),
});

// Integration tool response schema - same as native tool response
export const IntegrationToolResponseSchema = ToolWithEnabledSchema.extend({
  description: z.string(),
});

// Combined tools response
export const ToolsResponseSchema = z.object({
  data: z.object({
    native: z.array(NativeToolResponseSchema),
    mcp: z.array(McpToolResponseSchema),
    integrations: z.array(IntegrationToolResponseSchema),
  }),
});

// MCP servers response schema
export const McpServersResponseSchema = z.object({
  data: z.object({
    servers: z.record(
      z.string(),
      McpServerEntrySchema.extend({
        id: z.string(),
      }),
    ),
    tools: z.array(ToolWithServerIdSchema),
  }),
});

// Tool update request schema - reuses the base tool with enabled state
export const ToolUpdateRequestSchema = z.object({
  tools: z.array(ToolWithEnabledSchema),
});

export type NativeToolResponse = z.infer<typeof NativeToolResponseSchema>;
export type McpToolResponse = z.infer<typeof McpToolResponseSchema>;
export type IntegrationToolResponse = z.infer<
  typeof IntegrationToolResponseSchema
>;
export type ToolsResponse = z.infer<typeof ToolsResponseSchema>;
export type McpServersResponse = z.infer<typeof McpServersResponseSchema>;
export type ToolUpdateRequest = z.infer<typeof ToolUpdateRequestSchema>;

// ============================================================================
// Slack Integration Schema
// ============================================================================

// Slack workspace installation details
export const SlackWorkspaceInstallationSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  botToken: z.string(),
  botUserId: z.string(),
  installedAt: z.coerce.date(),
  installedBy: z.string(),
});

// Slack app credentials - flat structure
export const SlackAppCredentialsSchema = z.object({
  clientId: z.string(),
  clientSecret: z.string(),
  signingSecret: z.string(),
  appId: z.string().optional(), // Will be filled after first installation
});

// Slack app configuration for each agent
export const SlackAppConfigSchema = z.object({
  appId: z.string().optional(),
  clientId: z.string(),
  clientSecret: z.string(),
  signingSecret: z.string(),
  manifestUrl: z.string(),
  botName: z.string(),
  active: z.boolean().default(true),
  createdAt: z.coerce.date(),
  installedWorkspaces: z.array(SlackWorkspaceInstallationSchema).default([]),
});

export type SlackWorkspaceInstallation = z.infer<
  typeof SlackWorkspaceInstallationSchema
>;
export type SlackAppCredentials = z.infer<typeof SlackAppCredentialsSchema>;
export type SlackAppConfig = z.infer<typeof SlackAppConfigSchema>;

// ============================================================================
// Agent Schema - Single flat structure used everywhere
// ============================================================================

// This is THE agent configuration - stored in Durable Object
// Same shape used in forms, API, and storage
export const AgentConfigSchema = z.object({
  // Basic info
  name: AgentNameSchema,
  description: z.string().optional(),

  // Model configuration
  modelProvider: ModelProviderSchema,
  modelName: z.string().min(1, "Model name is required"),

  // Agent behavior
  systemPrompt: z
    .string()
    .min(10, "System prompt must be at least 10 characters"),
  temperature: z.number().min(0).max(2).default(0.7).optional(),
  maxTokens: z.number().int().positive().optional(),

  // Tool enablement - stores which tools are enabled for this agent
  tools: z.array(StoredToolSchema).default([]),

  // Slack integration
  slackApp: SlackAppConfigSchema.optional(),

  // Future extensibility
  settings: z.record(z.string(), z.any()).default({}).optional(),
});

export type AgentConfig = z.infer<typeof AgentConfigSchema>;

// ============================================================================
// Database Schema - Minimal metadata only
// ============================================================================

// What we store in D1 - just for listing/finding agents
export const AgentMetadataSchema = z.object({
  id: z.string().uuid(),
  userId: z.string(),
  name: AgentNameSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type AgentMetadata = z.infer<typeof AgentMetadataSchema>;

// ============================================================================
// API Schemas - Same structure with serialization
// ============================================================================

// For API responses, we combine metadata + config with ISO dates
export const AgentResponseSchema = z.object({
  // Metadata fields
  id: z.string().uuid(),
  userId: z.string(),
  createdAt: z.string(), // ISO string for JSON
  updatedAt: z.string(), // ISO string for JSON

  // Config fields (explicit to avoid circular references)
  name: AgentNameSchema,
  description: z.string().optional(),
  modelProvider: ModelProviderSchema,
  modelName: z.string().min(1, "Model name is required"),
  systemPrompt: z
    .string()
    .min(10, "System prompt must be at least 10 characters"),
  temperature: z.number().min(0).max(2).default(0.7).optional(),
  maxTokens: z.number().int().positive().optional(),
  tools: z.array(StoredToolSchema).default([]),
  slackApp: SlackAppConfigSchema.optional(),
  settings: z.record(z.string(), z.any()).default({}).optional(),
});

export type AgentResponse = z.infer<typeof AgentResponseSchema>;

// Create/Update requests - just the config without metadata, name is optional for creation
export const CreateAgentRequestSchema = AgentConfigSchema.extend({
  name: AgentNameSchema.optional(),
});
export const UpdateAgentRequestSchema = AgentConfigSchema.partial().refine(
  (data) => Object.keys(data).length > 0,
  { message: "At least one field must be provided for update" },
);

export type CreateAgentRequest = z.infer<typeof CreateAgentRequestSchema>;
export type UpdateAgentRequest = z.infer<typeof UpdateAgentRequestSchema>;

// ============================================================================
// Utility Functions
// ============================================================================

// Combine metadata and config for API responses
export function createAgentResponse(
  metadata: AgentMetadata,
  config: AgentConfig,
): AgentResponse {
  return {
    id: metadata.id,
    userId: metadata.userId,
    createdAt: metadata.createdAt.toISOString(),
    updatedAt: metadata.updatedAt.toISOString(),
    ...config,
  };
}

// ============================================================================
// Form Schema - Exactly the same as AgentConfig!
// ============================================================================

// The beauty of this approach - no separate form schema needed
export const AgentFormSchema = AgentConfigSchema;
export type AgentFormData = AgentConfig;

// ============================================================================
// Telemetry Schemas - AI SDK Telemetry Integration
// ============================================================================

import type { AttributeValue } from "@opentelemetry/api";

// Core span status types
export const TelemetrySpanStatusSchema = z.enum(["ok", "error", "cancelled"]);
export type TelemetrySpanStatus = z.infer<typeof TelemetrySpanStatusSchema>;

// Operation types from AI SDK
export const TelemetryOperationNameSchema = z.string();
export type TelemetryOperationName = z.infer<
  typeof TelemetryOperationNameSchema
>;

// Model providers
export const TelemetryModelProviderSchema = z.enum([
  "openai",
  "anthropic",
  "google",
]);
export type TelemetryModelProvider = z.infer<
  typeof TelemetryModelProviderSchema
>;

// AI operation details (extracted from OpenTelemetry attributes)
export const AiOperationDetailsSchema = z
  .object({
    modelProvider: z.string().optional(),
    modelName: z.string().optional(),
    inputTokens: z.number().int().min(0).optional(),
    outputTokens: z.number().int().min(0).optional(),
    totalTokens: z.number().int().min(0).optional(),
    finishReason: z.string().optional(),
    promptTokens: z.number().int().min(0).optional(),
    completionTokens: z.number().int().min(0).optional(),
  })
  .strict();

// Tool execution details (extracted from OpenTelemetry attributes)
export const AiToolExecutionSchema = z
  .object({
    toolName: z.string(),
    toolCallId: z.string(),
    parameters: z.record(z.string(), z.unknown()).optional(),
    result: z.record(z.string(), z.unknown()).optional(),
    executionTime: z.number().min(0).optional(),
    success: z.boolean(),
    errorMessage: z.string().optional(),
  })
  .strict();

// Error details
export const AiTelemetryErrorSchema = z
  .object({
    errorType: z.string(),
    errorMessage: z.string(),
    errorStack: z.string().optional(),
    retryCount: z.number().int().min(0),
    isRecoverable: z.boolean().optional(),
  })
  .strict();

export type AiOperationDetails = z.infer<typeof AiOperationDetailsSchema>;
export type AiToolExecution = z.infer<typeof AiToolExecutionSchema>;
export type AiTelemetryError = z.infer<typeof AiTelemetryErrorSchema>;

// Core telemetry span schema - API response format with JSON fields
export const TelemetrySpanSchema = z.object({
  id: z.string(),
  agentName: z.string(),
  spanId: z.string(),
  traceId: z.string(),
  parentSpanId: z.string().optional(),
  operationName: TelemetryOperationNameSchema,
  startTime: z.number().int().positive(),
  endTime: z.number().int().positive().optional(),
  duration: z.number().int().min(0).optional(),
  status: TelemetrySpanStatusSchema,
  createdAt: z.number().int().positive(),

  // JSON fields for variable data
  operationDetails: AiOperationDetailsSchema.optional(),
  toolCalls: z.array(AiToolExecutionSchema).optional(),
  errors: z.array(AiTelemetryErrorSchema).optional(),
  attributes: z.record(z.string(), z.any()).optional(),
});
export type TelemetrySpan = z.infer<typeof TelemetrySpanSchema>;

// Internal storage type - matches the database schema
export interface StoredTelemetrySpan {
  id: string;
  agentName: string;
  spanId: string;
  traceId: string;
  parentSpanId: string | null;
  operationName: string;
  startTime: number;
  endTime: number | null;
  duration: number | null;
  status: "ok" | "error" | "cancelled";
  createdAt: number;

  // JSON fields
  operationDetails?: AiOperationDetails | null;
  toolCalls?: AiToolExecution[] | null;
  errors?: AiTelemetryError[] | null;
  attributes?: Record<string, AttributeValue> | null;
}

// Query parameters for telemetry API
export const TelemetryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  operation: z.string().optional(),
  status: TelemetrySpanStatusSchema.optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
});
export type TelemetryQuery = z.infer<typeof TelemetryQuerySchema>;

// API response schemas
export const TelemetryApiResponseSchema = z.object({
  data: z.array(TelemetrySpanSchema),
});
export type TelemetryApiResponse = z.infer<typeof TelemetryApiResponseSchema>;

// WebSocket event schema for real-time telemetry updates
export const TelemetryEventSchema = z.object({
  type: z.literal("agent_telemetry_created"),
  agentName: z.string(),
  span: TelemetrySpanSchema,
});
export type TelemetryEvent = z.infer<typeof TelemetryEventSchema>;
